import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { tasksAPI } from '../services/api';
import { format, isAfter, startOfDay } from 'date-fns';

const UpcomingTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchAllTasks();
  }, []);

  const fetchAllTasks = async () => {
    try {
      setLoading(true);
      const response = await tasksAPI.getAllUserTasks();
      setTasks(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'primary';
      case 'todo':
        return 'default';
      default:
        return 'default';
    }
  };

  const filterTasksByTab = () => {
    const today = startOfDay(new Date());
    
    switch (currentTab) {
      case 0: // Todo
        return tasks
          .filter(task => task.status === 'todo' || task.status === 'in-progress')
          .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
      
      case 1: // Upcoming (future tasks)
        return tasks
          .filter(task => {
            const taskDate = new Date(task.deadline);
            return (task.status === 'todo' || task.status === 'in-progress') && 
                   isAfter(taskDate, today);
          })
          .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
      
      case 2: // Completed
        return tasks
          .filter(task => task.status === 'completed')
          .sort((a, b) => new Date(b.deadline) - new Date(a.deadline));
      
      default:
        return [];
    }
  };

  const filteredTasks = filterTasksByTab();

  const handleViewTask = (taskId, projectId) => {
    navigate(`/projects/${projectId}/tasks/${taskId}`);
  };

  const handleViewProject = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          All Tasks
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label={`To Do (${tasks.filter(t => t.status === 'todo' || t.status === 'in-progress').length})`} />
            <Tab label={`Upcoming (${tasks.filter(t => {
              const taskDate = new Date(t.deadline);
              return (t.status === 'todo' || t.status === 'in-progress') && 
                     isAfter(taskDate, startOfDay(new Date()));
            }).length})`} />
            <Tab label={`Completed (${tasks.filter(t => t.status === 'completed').length})`} />
          </Tabs>
        </Box>

        {filteredTasks.length === 0 ? (
          <Alert severity="info">
            No tasks found in this category.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {filteredTasks.map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task._id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: 6,
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Chip 
                        label={task.priority} 
                        color={getPriorityColor(task.priority)} 
                        size="small" 
                      />
                      <Chip 
                        label={task.status} 
                        color={getStatusColor(task.status)} 
                        size="small" 
                      />
                    </Box>

                    <Typography variant="h6" gutterBottom noWrap>
                      {task.title}
                    </Typography>

                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {task.description || 'No description'}
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Project: {task.project?.name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Deadline: {task.deadline ? format(new Date(task.deadline), 'MMM dd, yyyy') : 'No deadline'}
                      </Typography>
                      {task.assignedTo && task.assignedTo.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Assigned to: {task.assignedTo.map(u => u.name).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>

                  <CardActions>
                    <Button 
                      size="small" 
                      onClick={() => handleViewProject(task.project._id)}
                    >
                      View Project
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Layout>
  );
};

export default UpcomingTasks;
